import React, { Fragment } from 'react';
import { arrayOf, shape, string } from 'prop-types';
import _ from 'lodash';
import {
  Small,
  Button,
  HtmlParser,
  Paragraph,
  Heading,
  Link,
} from 'components/common';
import Experiment from 'components/abTest/Experiment';
import { getRuntimeEnvVariables } from 'helpers/Utils';
import AccordionPane from './AccordionPane';
import Offer from './Offer';
import AccordionSubSection from './AccordionSubSection';
import AccordionListItem from './AccordionListItem';
import Feature from '../Feature';
import MoreAboutProviderPanel from './MoreAboutProviderPanel';

import './AccordionPanel.scss';

export const staticTextKeys = [
  'featuresLabel',
  'offersLabel',
  'exclusiveOffersLabel',
  'offerConditionsLabel',
  'welcomeOffersLabel',
  'additionalOffersLabel',
  'aprFeesLabel',
  'aprLabel',
  'feesLabel',
  'labels',
  'moreAboutProviderShowMore',
  'moreAboutProviderShowLess',
  'moreAboutProviderTitle',
  'eligibilityLabel',
  'termsAndConditionsLabel',

  // TODO: Remove after exp end
  'newEntryPointToProductPageExpTitle',
];

const AccordionPanel = ({
  features,
  offers,
  apr,
  fees,
  eligibilityCriteria,
  documents,
  staticText,
  disclaimer,
  provider,
  termsAndConditions,
  excludedCountries,
  includedCountries,

  // TODO: Remove after exp end
  verticalEnum,
  titleRoute,
  list,
}) => (
  <div className="accordion-panel-wrapper">
    <div className="accordion-panel">
      {features && (
        <AccordionPane
          isExpanded={true}
          title={staticText.featuresLabel}
          dataSelectorId="features"
        >
          {(features.pros || []).map((pro, index) => (
            <Feature
              pro
              // eslint-disable-next-line
              key={index}
              htmlString={pro}
            />
          ))}
          {(features.cons || []).map((con, index) => (
            <Feature
              con
              // eslint-disable-next-line
              key={index}
              htmlString={con}
            />
          ))}
          {/* TODO: Remove after exp end */}
          <Experiment
            experimentId={getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_ID')}
            controlVariantId={getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_CONTROL')}
            variantIds={(getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_VARIANTS') || '').split(',')}
          >
            {(variantId) => {
              if (variantId === getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_VARIANT_1')
                && verticalEnum === 'CC'
                && titleRoute) {
                return (
                  <Link to={titleRoute && `${titleRoute}?entry=accordion${list ? `&from=${list}` : ''}`}>
                    <Button text leftIconName="info">
                      {staticText.newEntryPointToProductPageExpTitle}
                    </Button>
                  </Link>
                );
              }

              return null;
            }}
          </Experiment>
        </AccordionPane>
      )}
      {(_.get(offers, 'exclusiveOffers[0].reward.length') > 0 || _.get(offers, 'welcomeOffers[0].reward.length') > 0) && (
        <AccordionPane
          isExpanded={false}
          title={staticText.offersLabel}
          dataSelectorId="offers"
        >
          {_.get(offers, 'exclusiveOffers[0].reward.length') > 0 && (
            <Offer
              title={staticText.exclusiveOffersLabel}
              conditionsTitle={staticText.offerConditionsLabel}
              {...offers.exclusiveOffers[0]}
              labelOption={staticText.CC_PRODUCT_PAGE_OFFERS_OPTION_LABEL}
            />
          )}
          {_.get(offers, 'welcomeOffers[0].reward.length') > 0 && (
            <Offer
              title={staticText.welcomeOffersLabel}
              conditionsTitle={staticText.offerConditionsLabel}
              {...offers.welcomeOffers[0]}
              labelOption={staticText.CC_PRODUCT_PAGE_OFFERS_OPTION_LABEL}
            />
          )}
          {_.get(offers, 'additionalOffers[0].reward.length') > 0 && (
            <Offer
              title={staticText.additionalOffersLabel}
              conditionsTitle={staticText.offerConditionsLabel}
              {...offers.additionalOffers[0]}
              labelOption={staticText.CC_PRODUCT_PAGE_OFFERS_OPTION_LABEL}
            />
          )}
        </AccordionPane>
      )}
      {(Array.isArray(apr) || fees) && (
        <AccordionPane
          isExpanded={false}
          title={staticText.aprFeesLabel}
          dataSelectorId="apr-fees"
        >
          {apr && (
            <AccordionSubSection
              title={staticText.aprLabel}
            >
              {apr.map(({ type, description }, index) => (
                <AccordionListItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={type.key + index}
                  title={type.display}
                  description={description.shortDescription}
                />
              ))}
            </AccordionSubSection>
          )}
          {fees && (
            <AccordionSubSection
              title={staticText.feesLabel}
              dataSelectorId="fees-accordion"
            >
              {fees.map(({ type, description }, index) => (
                // eslint-disable-next-line
                <Fragment key={index}>
                  {description && description.shortDescription && (
                    <AccordionListItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={type.key + index}
                      title={type.display}
                      description={description.shortDescription}
                    />
                  )}
                </Fragment>
              ))}
            </AccordionSubSection>
          )}
        </AccordionPane>
      )}
      {eligibilityCriteria && (
        <AccordionPane
          isExpanded={true}
          title={staticText.eligibilityLabel}
          dataSelectorId="eligibility"
        >
          {eligibilityCriteria.map(({ type, description, value }, index) => (
            <AccordionListItem
              dataSelector={type.key}
              dataValue={value}
              // eslint-disable-next-line react/no-array-index-key
              key={type.key + index}
              title={type.display}
              description={description && description.shortDescription}
            />
          ))}
        </AccordionPane>
      )}
    </div>
    {documents && documents.length > 0 && (
      <div className="documents">
        {documents.map(({ type, file }, index) => {
          const { url } = file || {};
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={type.key + url + index}
              className="document-item"
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                <Button text leftIconName="download">
                  {type.display}
                </Button>
              </a>
            </div>
          );
        })}
      </div>
    )}
    {excludedCountries && excludedCountries.length > 0 && (
      <AccordionPane
        isExpanded
        title="Countries Covered"
        dataSelectorId="excludedCountries"
      >
        <div>
          <Heading as="h3" className="countriesCoveredSubHeading">Excluded Countries:</Heading>
          {excludedCountries.join(', ')}
        </div>
      </AccordionPane>
    )}
    {includedCountries && includedCountries.length > 0 && (
      <AccordionPane
        isExpanded
        title="Countries Covered"
        dataSelectorId="includedCountries"
      >
        <div>
          <Heading as="h3" className="countriesCoveredSubHeading">Included Countries:</Heading>
          {includedCountries.join(', ')}
        </div>
      </AccordionPane>
    )}
    {termsAndConditions && (
      <AccordionPane
        isExpanded={false}
        title={staticText.termsAndConditionsLabel}
        dataSelectorId="terms-and-conditions"
      >
        <Paragraph>
          <HtmlParser htmlString={termsAndConditions} />
        </Paragraph>
      </AccordionPane>
    )}
    {provider?.longDescription && (
      <MoreAboutProviderPanel
        showMoreLabel={staticText.moreAboutProviderShowMore}
        showLessLabel={staticText.moreAboutProviderShowLess}
        title={staticText.moreAboutProviderTitle}
        moreAboutProvider={provider.longDescription}
      />
    )}
    {disclaimer && (
      <div className="disclaimer">
        <Small secondary>
          {disclaimer}
        </Small>
      </div>
    )}
  </div>
);

AccordionPanel.propTypes = {
  features: shape({
    pros: arrayOf(string).isRequired,
    cons: arrayOf(string).isRequired,
  }),
  offers: shape({
  }),
  apr: arrayOf(
    shape({
      type: shape({}),
      value: string,
    })
  ),
  fees: arrayOf(
    shape({
      type: shape({}),
      value: string,
    })
  ),
  eligibilityCriteria: arrayOf(
    shape({
      type: shape({}),
      value: string,
    })
  ),
  documents: arrayOf(shape({
    name: string,
    url: string,
  })),
  labels: shape({}),
  disclaimer: string,
  provider: shape({
    longDescription: string,
  }),
  termsAndConditions: string,
  staticText: shape({}),
  excludedCountries: arrayOf(string),
  includedCountries: arrayOf(string),
  verticalEnum: string,
  titleRoute: string,
  list: string,
};

AccordionPanel.defaultProps = {
  features: null,
  offers: null,
  apr: null,
  fees: null,
  eligibilityCriteria: null,
  disclaimer: null,
  documents: [],
  labels: {},
  termsAndConditions: null,
  provider: null,
  staticText: {},
  excludedCountries: [],
  includedCountries: [],
  verticalEnum: null,
  titleRoute: null,
  list: null,
};

export default AccordionPanel;
